<template>
  <div>
    <el-table
      style="width: 100%"
      :data="attributionsInPage"
      :default-sort="{ prop: 'order.createdAt', order: 'descending' }"
      @row-click="goToAttributionDetails"
    >
      <el-table-column label="Date" width="100" sortable prop="order.createdAt">
        <template slot-scope="scope">
          <span>{{ formatDate(scope.row.order.createdAt) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Société / Client" width="200">
        <template slot-scope="scope">
          <div class="d-flex">
            <span>{{ fullName(scope.row) }}</span>
            <div
              class="text-primary"
              style="margin-left: 0.5rem"
              @click.stop="openParticipantsTabs(scope.row.order)"
            >
              <i class="icon-user"></i>
              <span
                v-if="
                  scope.row.order.orderGroup &&
                  scope.row.order.orderGroup.orders &&
                  scope.row.order.orderGroup.orders.length > 1
                "
              >
                {{ scope.row.order.orderGroup.orders.length }}
              </span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        label="CP"
        width="80"
        sortable
        prop="order.refClient.zipcode"
      >
      </el-table-column>
      <el-table-column
        label="Ville"
        sortable
        prop="order.refClient.city"
        width="180"
      >
      </el-table-column>
      <el-table-column label="Pack" width="150">
        <template slot-scope="scope">
          <a
            class="order-link"
            @click.prevent.stop="goToOrder(scope.row.order)"
          >
            {{ formatPack(scope.row.order.session) }}
            <i
              class="fa fa-cart-plus"
              :class="
                scope.row.order.status === 'payed' ||
                scope.row.order.status === 'partial'
                  ? 'text-success'
                  : 'text-dark'
              "
            />
            <i
              v-if="hasPaymentFailed(scope.row.order.payments)"
              class="text-danger fa fa-exclamation-circle ml-0-5"
            />
          </a>
        </template>
      </el-table-column>
      <el-table-column label="Montant" width="120" sortable prop="order.price">
        <template slot-scope="scope">
          {{ formatPrice(scope.row.order.price) }}
        </template>
      </el-table-column>
      <el-table-column
        width="120"
        label="Concept"
        sortable
        prop="order.concept"
      >
        <template slot-scope="scope">
          <span v-html="conceptLogo(scope.row.order.concept, concepts)"></span>
        </template>
      </el-table-column>
      <el-table-column
        label="Commercial"
        prop="order.commercials.userEmail"
        width="200"
        align="center"
        sortable
      >
        <template slot-scope="scope">
          <template v-for="(rel, index) of scope.row.order.commercials">
            <template>
              <CommercialRel :key="`com-${index}`" :relation="rel" />
            </template>
          </template>
        </template>
      </el-table-column>
      <el-table-column
        label="Coach manager / Marchés public"
        prop="order.coachManager.userEmail"
        width="280"
        align="center"
        sortable
      >
        <template slot-scope="scope">
          <template v-for="(rel, index) of scope.row.order.coachManagers">
            <template>
              <CoachManagerRel :key="`com-${index}`" :relation="rel" />
            </template>
          </template>
        </template>
      </el-table-column>
      <el-table-column min-width="220" label="" accesskey="">
        <template slot-scope="scope">
          <AttributionStatusBadge
            :attribution="scope.row"
            @details="goToAttributionDetails"
            @delete="deleteHandler"
          />
        </template>
      </el-table-column>
      <el-table-column
        label="1er attrib."
        width="120"
        sortable
        prop="firstSendAt"
      >
        <template slot-scope="scope">
          {{ getDelay(scope.row.firstSendAt) }}
        </template>
      </el-table-column>
      <el-table-column
        label="1er paiement"
        width="150"
        sortable
        prop="firstPaiementAt"
      >
        <template slot-scope="scope">
          {{ getDelayFirstPayment(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column width="220" label="Notes Admin">
        <template slot-scope="scope">
          <NoteModal
            :notes="scope.row.order.adminNotes"
            :key="scope.row.order.id"
            permission="ATTRIBUTIONS_WRITE"
            @showComment="showComment(scope.row.order)"
            v-if="hasPermission($store.state.user, 'ATTRIBUTIONS_WRITE')"
          />
          <span v-else>{{ scope.row.order.adminNotes }}</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="hasPermission($store.state.user, 'ATTRIBUTIONS_WRITE')"
        width="80"
        label="Attrib"
        align="center"
      >
        <template slot-scope="scope">
          <button
            class="btn btn-sm button-transparent"
            @click.stop="allowAttribution(scope.row)"
          >
            <i class="icon-plus"></i>
          </button>
        </template>
      </el-table-column>
      <el-table-column width="80" label="Rappel" align="center">
        <template slot-scope="scope">
          <button
            class="btn btn-sm"
            :class="{ 'btn-success': scope.row.recall }"
            @click.stop="updateRecall(scope.row)"
            v-if="hasPermission($store.state.user, 'ATTRIBUTIONS_WRITE')"
          >
            <i v-if="scope.row.recall" class="icon-check"></i>
            <i v-else class="icon-phone"></i>
          </button>
          <span v-else>
            <i v-if="scope.row.recall" class="icon-check"></i>
            <i v-else class="icon-phone"></i>
          </span>
        </template>
      </el-table-column>
    </el-table>

    <paginate
      :page-count="count"
      :click-handler="paginationHandler"
      v-if="count"
      prev-text="Précédent"
      next-text="Suivant"
      container-class="pagination"
    ></paginate>
  </div>
</template>

<script>
import Paginate from "vuejs-paginate";
import { mapState } from "vuex";
import moment from "moment";
import NoteModal from "@/components/form/NoteModal";
import CommercialRel from "@/components/utils/CommercialRel";
import CoachManagerRel from "@/components/coach/CoachManagerRel";
import AttributionStatusBadge from "@/components/attribution/AttributionStatusBadge";

export default {
  components: {
    Paginate,
    NoteModal,
    CommercialRel,
    CoachManagerRel,
    AttributionStatusBadge,
  },

  props: {
    attributions: {
      type: Array,
      default: null,
    },
    orders: {
      type: Boolean,
      default: false,
    },
    payments: {
      type: Boolean,
      default: false,
    },
    overridePage: {
      type: Number,
      default: 1,
    },
    rows: {
      type: Number,
      default: 20,
    },
  },

  data() {
    return {
      page: this.$route.query.page || this.overridePage,
    };
  },

  computed: {
    ...mapState({
      concepts: (state) => state.concepts,
    }),

    attributionsInPage() {
      return this.attributions.slice(
        this.page * this.rows - this.rows,
        this.page * this.rows,
      );
    },

    count() {
      return Math.ceil(this.attributions.length / this.rows);
    },
  },

  methods: {
    fullName(value) {
      if (value.refClient) {
        return value.refClient.fullName;
      } else if (value.client) {
        return value.client.fullName;
      } else if (value.session) {
        return value.session.client.fullName;
      } else if (value.order && value.order.refClient) {
        return value.order.refClient.fullName;
      } else if (value.order && value.order.client) {
        return value.order.client.fullName;
      }
    },

    paginationHandler(page) {
      this.page = page;
    },

    deleteHandler(value) {
      let type = "orders";

      if (this.payments) {
        type = "payments";
      } else if (value.order.status === "pending") {
        type = "notPayed";
      }

      this.$emit("delete", value, type);
    },

    goToAttributionDetails(attribution) {
      const route = this.$router.resolve({
        name: "attributions-order",
        params: {
          id: attribution.orderId,
        },
      });

      window.open(route.href, "_blank");
    },

    goToClient(id) {
      const route = this.$router.resolve({
        name: "client-details",
        params: { id },
      });
      window.open(route.href, "_blank");
    },

    getDelayFirstPayment(attribution) {
      if (!attribution.order.payments || !attribution.order.payments[0]) {
        return "inconnu";
      }

      return this.getDelay(attribution.order.payments[0].createdAt);
    },

    getDelay(value) {
      if (!value) {
        return "inconnu";
      }

      const now = moment();
      const duration = moment.duration(now.diff(value));
      const hours = duration.asHours();
      const minutes = duration.asMinutes();

      return hours > 1
        ? `${Math.round(hours)} h`
        : `${Math.round(minutes)} min`;
    },
    updateRecall(value) {
      value.recall = !value.recall;

      this.$api.put(`/attributions/${value.id}`, {
        recall: value.recall,
      });

      this.$emit("moveAttribution", value);
    },

    goToOrder(order) {
      const route = this.$router.resolve({
        name: "order-details",
        params: {
          id: order.id,
        },
      });

      window.open(route.href, "_blank");
    },

    allowAttribution(attribution) {
      this.$emit("allowAttribution", attribution);
    },

    showComment(order) {
      this.$emit("showComment", order);
    },

    hasPaymentFailed(payments) {
      return (
        payments.filter((payment) => {
          return !payment.captured && payment.failed;
        }).length > 0
      );
    },

    openParticipantsTabs(order) {
      let ids = [order.refClient.id];

      if (order.orderGroup?.orders?.length > 0) {
        ids = [];

        for (const participantOrder of order.orderGroup.orders) {
          ids.push(participantOrder.refClient.id);
        }
      }

      ids.forEach((id) => {
        this.goToClient(id);
      });
    },
  },
};
</script>

<style lang="css" scoped>
.user-link {
  padding-left: 5px;
}
.table-link,
.order-link {
  cursor: pointer;
  text-decoration: none;
}
.text-dark {
  color: black;
}

.button-transparent {
  background-color: transparent;
}
.ml-0-5 {
  margin-left: 0.5rem;
}
</style>
